import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  addCartLine,
  closeCartDrawer,
  openCartDrawer,
  removeCartLineAtIndex,
  setCartLineQtyAtIndex,
  clearCart,
  selectCartItems,
  selectCartDrawerOpen,
} from '../store/actions/cartActions';

/** Cart + bag drawer — backed by Redux; persisted via `store/index.js` subscribe. */
export function useCart() {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectCartItems);
  const isOpen = useAppSelector(selectCartDrawerOpen);

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, line) =>
          sum + Number(line.product?.price ?? 0) * Number(line.qty ?? 0),
        0,
      ),
    [items],
  );

  const count = useMemo(
    () => items.reduce((sum, line) => sum + Number(line.qty ?? 0), 0),
    [items],
  );

  const open = useCallback(() => {
    dispatch(openCartDrawer());
  }, [dispatch]);

  const close = useCallback(() => {
    dispatch(closeCartDrawer());
  }, [dispatch]);

  const add = useCallback(
    (payload) => {
      dispatch(addCartLine(payload));
    },
    [dispatch],
  );

  const remove = useCallback(
    (idx) => {
      dispatch(removeCartLineAtIndex(idx));
    },
    [dispatch],
  );

  const setQty = useCallback(
    (idx, qty) => {
      dispatch(setCartLineQtyAtIndex({ index: idx, qty }));
    },
    [dispatch],
  );

  const clear = useCallback(() => {
    dispatch(clearCart());
  }, [dispatch]);

  return useMemo(
    () => ({
      items,
      isOpen,
      open,
      close,
      add,
      remove,
      setQty,
      clear,
      subtotal,
      count,
    }),
    [
      items,
      isOpen,
      open,
      close,
      add,
      remove,
      setQty,
      clear,
      subtotal,
      count,
    ],
  );
}
